import { useLinkageAmount, useLinkageBusinessAPI } from '../LinkageCreationContext'
import CustomButton from '../../../../../ui/CustomButton'
import { useContext, useEffect, useRef, useState } from 'react'
import GlobalContext from '../../../../../../../context/global-context'
//import { BankAPI } from '../../../../../../../api/bancos.api'
import { Title } from '../../../../../ui'
import ResizableModal from '../../../../../ui/ResizableModal'
import { OrganizationAPI } from '../../../../../../../api/organization.api'
import TablaRendicionGasto from './TablaRendicionGasto'
import Lottie from 'lottie-react'
import { Box } from '@mui/material'
import LoaderMain from '../../../../../../../assets/svgIcons/loaderMain.json'
import { CentroCostosAPI } from '../../../../../../../api/centroCostos.api'
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'
import { sileo } from 'sileo'
import * as XLSX from 'xlsx'




const isValidRendicionRut = value => {
  const rut = String(value ?? '').trim()
  if (!/^(?:\d{7,8}|\d{1,2}\.\d{3}\.\d{3})-[\dkK]$/.test(rut)) return false
  const [number, digit] = rut.replace(/\./g, '').toUpperCase().split('-')
  if (Number(number) === 0) return false
  let sum = 0
  let multiplier = 2
  for (let index = number.length - 1; index >= 0; index--) {
    sum += Number(number[index]) * multiplier
    multiplier = multiplier === 7 ? 2 : multiplier + 1
  }
  const remainder = 11 - (sum % 11)
  return digit === (remainder === 11 ? '0' : remainder === 10 ? 'K' : String(remainder))
}

const validateRendicionWorkbook = workbook => {
  const sheet = workbook.Sheets[workbook.SheetNames[0]]
  if (!sheet?.['!ref']) return ['El archivo Excel está vacío.']
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: true, range: 0 })
  const headers = rows[0] || []
  const rutColumns = headers.flatMap((header, index) =>
    /rut/i.test(String(header).replace(/[.\s_-]/g, '')) ? [index] : []
  )
  if (!rutColumns.length) {
    return ['No se encontró una columna RUT. Utiliza la plantilla de rendición de gastos.']
  }
  const errors = []
  rows.slice(1).forEach((row, index) => {
    if (!row.some(value => String(value ?? '').trim() !== '')) return
    rutColumns.forEach(column => {
      const value = String(row[column] ?? '').trim()
      if (!value) {
        errors.push(`Fila ${index + 2}, ${headers[column]}: debes ingresar un RUT. El campo no puede estar vacío.`)
        return
      }
      if (!isValidRendicionRut(value)) {
        errors.push(`Fila ${index + 2}, ${headers[column]}: RUT "${value}" inválido. Revisa el formato (12345678-5) y el dígito verificador.`)
      }
    })
  })
  return errors
}

const validateRendicionExcel = async file => {
  try {
    const buffer = await file.arrayBuffer()
    return validateRendicionWorkbook(XLSX.read(buffer, { type: 'array' }))
  } catch {
    return ['No se pudo leer el archivo Excel. Verifica que sea un archivo .xlsx o .xls válido.']
  }
}

const getRendicionResponseError = response => {
  const body = response?.data ?? response
  const messages = typeof body === 'string'
    ? [body]
    : [body?.error, body?.message, body?.errors].flat().filter(Boolean).map(value =>
      typeof value === 'string' ? value : JSON.stringify(value)
    )
  const failed = body?.success === false || Number(body?.statusCode) >= 400 ||
    (body?.error && body.error !== false) || (Array.isArray(body?.errors) && body.errors.length > 0)
  const errorMessage = messages.find(message => /\b\w*exception\b|^error\s*:|rut characters invalid/i.test(message))
  if (!failed && !errorMessage) return null
  const message = errorMessage || messages.join('\n') || 'No se pudo cargar la rendición de gastos.'
  if (/rut characters invalid/i.test(message)) {
    return 'No se pudo cargar el Excel: el servidor rechazó un RUT vacío o inválido. Revisa la columna RUT de todas las filas.'
  }
  return message
}

const CENTER_COLORS = [
  '#00C6B3',
  '#FF6B6B',
  '#4F46E5',
  '#F59E0B',
  '#EC4899',
  '#10B981',
  '#3B82F6',
  '#A855F7',
  '#F97316',
  '#22C55E'
]
const RENDICION_GASTOS_TEMPLATE_TYPE = 'rendicion-gastos'
const RENDICION_GASTOS_TEMPLATE_FILE_NAME = 'RendicionDeGastos.xlsx'

const downloadTemplateFile = (data, fileName) => {
  const url = window.URL.createObjectURL(new Blob([data]))
  const link = document.createElement('a')

  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const buildCentroCostoPayload = rows => {
  return rows
    .filter(row => row.centroCostoId && row.porcentaje > 0)
    .map(row => ({
      idCentroCosto: Number(row.centroCostoId),
      porcentajeCentroCosto: row.porcentaje
    }))
}

const InputField = ({ label, placeholder, type, value, onChange, disabled = false }) => (
  <div>
    <label className='block text-sm font-sans font-medium text-gray-700'>{label}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`mt-1 block w-full rounded-full border-gray-300 shadow-sm sm:text-sm font-sans
        ${
          disabled
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
            : 'focus:border-indigo-500 focus:ring-indigo-500'
        }
      `}
    />
  </div>
)

const TextAreaField = ({ label, value, onChange, ...props }) => (
  <div>
    <label className='block text-sm font-sans font-medium text-gray-700'>{label}</label>
    <textarea
      className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
      value={value}
      onChange={onChange}
      maxLength={50}
      required
      {...props}
    />
    <p className='text-xs text-gray-500 mt-1 font-sans'>{value.length}/50 caracteres</p>
  </div>
)

const getMassiveUploadRawMessage = source => {
  const rawMessage =
    typeof source === 'string'
      ? source
      : source?.response?.data?.message ||
        source?.response?.data?.error ||
        (typeof source?.response?.data === 'string' ? source.response.data : '') ||
        source?.data?.message ||
        source?.data?.error ||
        (typeof source?.data === 'string' ? source.data : '') ||
        source?.message ||
        ''

  return String(rawMessage).replace(/[\r\n]+/g, ' ').trim()
}

const getMassiveUploadErrorMessage = error =>
  (error instanceof Error && !error.response ? error.message : getMassiveUploadRawMessage(error)) ||
  'Error al subir archivo'

const getFirstNonEmptyValue = (...values) => {
  for (const value of values) {
    if (value == null) continue
    const normalizedValue = typeof value === 'string' ? value.trim() : value
    if (normalizedValue !== '') return normalizedValue
  }
  return ''
}

const formatDateForInput = value => {
  if (!value) return ''
  const parsedDate = new Date(value)
  return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().split('T')[0]
}

const normalizeMontoValue = value => {
  if (value == null) return ''
  return String(value).replace(/\D/g, '')
}

const normalizeFilaGasto = fila => ({
  // El backend legacy guardaba "Concepto" donde la UI actual usa "tipoDocumento".
  tipoDocumento: getFirstNonEmptyValue(
    fila?.tipoDocumento,
    fila?.descripcion,
    fila?.concepto,
    fila?.Concepto,
    fila?.['Concepto']
  ),
  fechaDocumento: formatDateForInput(
    getFirstNonEmptyValue(
      fila?.fechaDocumento,
      fila?.fecha,
      fila?.Fecha,
      fila?.['Fecha Documento'],
      fila?.['Fecha']
    )
  ),
  folio: getFirstNonEmptyValue(
    fila?.folio,
    fila?.numeroDocumento,
    fila?.NumeroDocumento,
    fila?.['Numero Documento'],
    fila?.id
  ),
  // El backend legacy guardaba "Tipo Documento" donde la UI actual usa "contraparte".
  contraparte: getFirstNonEmptyValue(
    fila?.contraparte,
    fila?.tipoDoc,
    fila?.tipoDocumentoNombre,
    fila?.TipoDocumento,
    fila?.['Tipo Documento']
  ),
  monto: normalizeMontoValue(
    getFirstNonEmptyValue(
      fila?.monto,
      fila?.montoClp,
      fila?.Monto,
      fila?.['Monto']
    )
  ),
  notas: getFirstNonEmptyValue(
    fila?.notas,
    fila?.Notas,
    fila?.['Notas']
  )
})

const MassiveUploadContent = ({
  archivoExcel,
  onFileSelect,
  errorMessage,
  isDownloadingTemplate = false,
  onTemplateDownload
}) => {
  const fileInputRef = useRef(null)

  const handleFileChange = e => {
    if (e.target.files?.[0]) {
      onFileSelect(e.target.files[0])
      e.target.value = ''
    }
  }

  const handleRemoveFile = () => {
    onFileSelect(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className='w-full rounded-3xl border border-gray-200 bg-white p-6 shadow-md'>
      <div className='flex flex-col gap-6'>
        <div>
          <h3 className='text-xl font-semibold font-sans text-gray-900'>
            Carga masiva por Excel
          </h3>
          {/*<p className='mt-2 text-sm font-sans text-gray-500'>
            Esta vista deja preparada la estructura para importar varias rendiciones desde un
            archivo Excel.
          </p>*/}
        </div>

        {errorMessage && (
          <div className='rounded-2xl border border-red-200 bg-red-100 px-4 py-3 text-xs font-semibold text-red-700 shadow-sm'>
            <div className='flex items-center gap-2'>
              <svg className='h-5 w-5 shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              <span className='whitespace-pre-wrap'>{errorMessage}</span>
            </div>
          </div>
        )}

        <div className='grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]'>
          <div className='rounded-2xl border border-slate-200 bg-slate-50 p-5'>
            <p className='text-sm font-semibold font-sans text-gray-800'>Para ingresar datos de forma masiva, por favor sigue las siguientes instrucciones</p>
            <ol className='mt-3 list-decimal space-y-3 pl-5 text-sm font-sans text-gray-600'>
              <li>
                <div className='flex flex-col items-start gap-2'>
                  <CustomButton
                    type='button'
                    title={isDownloadingTemplate ? 'Descargando...' : 'Descargar plantilla Excel'}
                    color='accent2'
                    className='rounded-full uppercase text-xs'
                    disabled={isDownloadingTemplate}
                    onClick={onTemplateDownload}
                  />
                </div>
              </li>
              <li>Ingresa los datos en la plantilla descargada.</li>
              <li>Guarda el archivo en formato Excel (.xlsx o .xls).</li>
              <li>Selecciona el archivo y haz clic en Guardar.</li>
            </ol>

            <p className='mt-3 text-xs font-sans text-gray-500'>
              El archivo Excel es obligatorio para la carga masiva.
            </p>
          </div>

          <div className='rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-6'>
            <div className='flex h-full flex-col items-center justify-center gap-4 text-center'>
              <div className='space-y-2'>
                <p className='text-sm font-semibold font-sans text-gray-800'>Archivo Excel</p>
                <p className='text-xs font-sans text-gray-500'>
                  Selecciona la plantilla completada para procesar la carga masiva de rendiciones de gasto.
                </p>
              </div>

              <input
                ref={fileInputRef}
                type='file'
                accept='.xlsx,.xls'
                className='hidden'
                onChange={handleFileChange}
              />

              {archivoExcel ? (
                <div className='w-full rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-left'>
                  <p className='text-xs font-semibold font-sans text-primary'>Archivo seleccionado</p>
                  <p className='mt-1 break-all text-sm font-sans text-gray-700'>{archivoExcel.name}</p>
                  <button
                    type='button'
                    onClick={handleRemoveFile}
                    className='mt-3 text-xs font-semibold font-sans text-red-500 hover:text-red-600'
                  >
                    Quitar archivo
                  </button>
                </div>
              ) : (
                <div className='w-full rounded-2xl bg-white/80 px-4 py-6'>
                  <p className='text-sm font-sans text-gray-500'>
                    Aun no has seleccionado un archivo Excel para importar.
                  </p>
                </div>
              )}

              <div className='flex w-full flex-col gap-3'>
                <CustomButton
                  type='button'
                  title={archivoExcel ? 'Cambiar archivo' : 'Seleccionar archivo'}
                  color='primary'
                  className='w-full rounded-full uppercase text-sm'
                  onClick={() => fileInputRef.current?.click()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const isForbiddenCentroName = name =>
  name?.trim().toLowerCase() === 'estado de resultados consolidado'.toLowerCase()

const RendicionDeGastos = ({
  open,
  setOpen,
  type = 'egreso',
  setSwitchDate,
  idLibro,
  fVenc,
  folio,
  action
}) => {
  const canUseMassiveUpload = action === 'crear'
  const filaVacia = {
    tipoDocumento: '',
    fechaDocumento: '',
    folio: '',
    contraparte: '',
    monto: ''
  }
  const initialModoCarga = canUseMassiveUpload ? 'masiva' : 'manual'

  const [movimientoSeleccionado] = useState(null)
  const [fecha, setFecha] = useState('')
  const [glosa, setGlosa] = useState('')
  const [monto, setMonto, bankAmountLocked] = useLinkageAmount('')
  const [rut, setRut] = useState('')
  const [filasGastos, setFilasGastos] = useState( Array.from({ length: 5 }, () => ({ ...filaVacia })) )
  const [modoCarga, setModoCarga] = useState(initialModoCarga)
  const [archivoExcel, setArchivoExcel] = useState(null)
  const excelValidationRequest = useRef(0)
  const [isValidatingExcel, setIsValidatingExcel] = useState(false)
  const [excelValidationErrors, setExcelValidationErrors] = useState([])
  const [errorMessage, setErrorMessage] = useState(null)
  const [razonSocial, setRazonSocial] = useState('')
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
  const businessAPI = useLinkageBusinessAPI()
  const { ui, currentBusiness } = useContext(GlobalContext)
  const organizationAPI = new OrganizationAPI()
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(false)
  const [centroCostoError, setCentroCostoError] = useState('')
  const [initialCentroCostoPayload, setInitialCentroCostoPayload] = useState(null)
  const centroCostosAPI = new CentroCostosAPI()
  const [centroCostos, setCentroCostos] = useState([])
  const [razonesS, setRazonesS] = useState({})
  const [centroCostoTemporalidad, setCentroCostoTemporalidad] = useState(0)
  const [initialCentroTemporalidad] = useState(0)
  const buildInitialRows = (count = 2) =>
    Array.from({ length: count }).map((_, i) => ({
      centroCostoId: '',
      porcentaje: 0,
      color: CENTER_COLORS[i % CENTER_COLORS.length]
    }))
  const [centrosCostoRows, setCentrosCostoRows] = useState(buildInitialRows(2))
  const getAvailableCentroCostosCount = () => {
    if (!Array.isArray(centroCostos)) return 0
    return centroCostos.filter(cc => !isForbiddenCentroName(cc.name)).length
  }

  const isManualMode = modoCarga === 'manual'

  const handleClose = () => {
    excelValidationRequest.current += 1
    setIsValidatingExcel(false)
    setExcelValidationErrors([])
    setModoCarga(initialModoCarga)
    setArchivoExcel(null)
    setErrorMessage(null)
    setOpen(false)
  }

  const handleArchivoExcelChange = async file => {
    const request = ++excelValidationRequest.current
    setArchivoExcel(file)
    setErrorMessage(null)
    setExcelValidationErrors([])
    setIsValidatingExcel(Boolean(file))
    if (!file) return
    const errors = await validateRendicionExcel(file)
    if (request !== excelValidationRequest.current) return
    setExcelValidationErrors(errors)
    setErrorMessage(errors.length ? errors.join('\n') : null)
    setIsValidatingExcel(false)
  }

  const handleDownloadTemplate = async () => {
    if (isDownloadingTemplate) return

    setIsDownloadingTemplate(true)

    try {
      const response = await businessAPI.downloadRemunerationTemplate(RENDICION_GASTOS_TEMPLATE_TYPE)
      downloadTemplateFile(response.data, RENDICION_GASTOS_TEMPLATE_FILE_NAME)
    } catch (error) {
      console.error('Error al descargar plantilla:', error)
      sileo.error({
        title: 'Error de Descarga',
        description: 'No se pudo descargar la plantilla. Intenta nuevamente.'
      })
    } finally {
      setIsDownloadingTemplate(false)
    }
  }

  const functionGetData = async () => {
    try {
      setLoading(true)
      const response = await organizationAPI.getDataLibroContable(
        currentBusiness?.id,
        idLibro,
        type.trim()
      )
      setData(response.data)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const getRazones = async () => {
    try {
      const response = await organizationAPI.getRazonSocial(currentBusiness?.id)
      setRazonesS(response.data)
    } catch (error) {
      console.error(error)
    } finally {
      //
    }
  }

  useEffect(() => {
    if (open && idLibro) {
      functionGetData()
      getRazones()
    }
  }, [open, idLibro])

  useEffect(() => {
    if (open) {
      setModoCarga(initialModoCarga)
      setArchivoExcel(null)
      setErrorMessage(null)
    }
  }, [initialModoCarga, open])

  useEffect(() => {
    setErrorMessage(null)
  }, [modoCarga])

  const getCentroCostos = async () => {
    try {
      const response = await centroCostosAPI.getCentroCostos(currentBusiness.id)
      setCentroCostos(response.data)
    } catch (error) {
      console.error('Error al cargar los centros de costo:', error)
      throw error
    }
  }

  useEffect(() => {
    getCentroCostos()
  }, [])

  useEffect(() => {
    if (data) {
      if (data.fecha) {
        const parsedDate = new Date(data.fecha)
        const formattedDate = parsedDate.toISOString().split('T')[0]
        setFecha(formattedDate)
      }
      if (data.glosa) setGlosa(data.glosa)
      if (data.montoTotal) setMonto(data.montoTotal.toString())
      const rendidor = Array.isArray(data.extra)
        ? data.extra.find(item => item?.rendidor)?.rendidor
        : data.extra?.rendidor
      setRazonSocial(rendidor || data.razonSocial || '')
      if (data.rut) setRut(data.rut)
      if (Array.isArray(data.centro) && data.centro.length > 0) {
        const rows = data.centro.map((c, index) => ({
          centroCostoId: c.centroCostoId,
          porcentaje: c.porcentajeCentroCosto,
          color: CENTER_COLORS[index % CENTER_COLORS.length]
        }))
        setCentrosCostoRows(rows)
        setInitialCentroCostoPayload(buildCentroCostoPayload(rows))
      } else {
        const initialRows = buildInitialRows(2)
        setCentrosCostoRows(initialRows)
        setInitialCentroCostoPayload(buildCentroCostoPayload(initialRows))
      }
      const rawFilasGastos = Array.isArray(data.extra?.filasGastos)
        ? data.extra.filasGastos
        : Array.isArray(data.extra)
          ? data.extra
          : []

      if (rawFilasGastos.length > 0) {
      const filas = rawFilasGastos.map(normalizeFilaGasto)

      setFilasGastos(filas)
    } else {
      // fallback: si no vienen filas, iniciar con 5 vacías
      setFilasGastos(
        Array.from({ length: 5 }, () => ({ ...filaVacia }))
      )
    }
    }
  }, [data])

  const handleDatos = async () => {
    if (!fecha || !monto || !rut || !razonSocial || !glosa) {
      alert('Todos los campos son obligatorios excepto folio')
      return
    }
    const filasNormalizadas = filasGastos
    .filter(f => f.tipoDocumento || f.fechaDocumento || f.folio ||f.contraparte || f.monto || f.notas )
    .map(fila => ({
      tipoDocumento: fila.tipoDocumento,
      fechaDocumento: fila.fechaDocumento || null,
      folio: fila.folio,
      contraparte: fila.contraparte,
      monto: Number(fila.monto) || 0,
      notas: fila.notas
    }))

    const payload = {
      fecha: fecha,
      transaccionId: action === 'replicar' ? null : movimientoSeleccionado?.id,
      razonSocial: razonSocial || null,
      rut: rut,
      rutId: null,
      nombreComprobante: null,
      nombreComprobanteId: null,
      glosa: glosa || null,
      respaldoId: null,
      monto: Number(monto),
      extra: {
        filasGastos: filasNormalizadas
      }
    }
    try {
      setLoading(true)
      const response = await businessAPI.postPagoArriendo(
        currentBusiness.id,
        payload,
        'rendicion-gasto'
      )
      console.info(response)
      setSwitchDate(true)
      handleClose()
    } catch (error) {
      console.error('Error al agregar comprobante:', error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    ui.setLoader({ visible: true })
    setCentroCostoError('')

    const centroCostoPayload = buildCentroCostoPayload(centrosCostoRows)
    const temporalidad = Number(centroCostoTemporalidad ?? 0) || 0

    const hasCentroCostoChanges = initialCentroCostoPayload
        ? JSON.stringify(centroCostoPayload) !== JSON.stringify(initialCentroCostoPayload) ||
          temporalidad !== initialCentroTemporalidad
        : centroCostoPayload.length > 0

      if (hasCentroCostoChanges && centroCostoPayload.length > 0) {
        const totalPorcentaje = centroCostoPayload.reduce(
          (acc, item) => acc + (item.porcentajeCentroCosto || 0),
          0
        )

      if (totalPorcentaje !== 100) {
        setCentroCostoError("La suma de porcentajes debe dar 100%")
        ui.setLoader({ visible: false })
        return
      }

      const ids = centroCostoPayload.map(i => i.idCentroCosto)
      if (new Set(ids).size !== ids.length) {
        setCentroCostoError('No puedes repetir centros de costo')
        ui.setLoader({ visible: false })
        return
      }
    }

    const filasNormalizadas = filasGastos
    .filter(f => f.tipoDocumento || f.fechaDocumento || f.folio ||f.contraparte || f.monto || f.notas )
    .map(f => ({
      tipoDocumento: f.tipoDocumento,
      fechaDocumento: f.fechaDocumento || null,
      folio: f.folio,
      contraparte: f.contraparte,
      monto: Number(f.monto) || 0,
      notas: f.notas
    }))

    const payload = {
      fecha,
      glosa,
      monto: Number(monto),
      razonSocial,
      rut,
      nombreComprobante: data.nombreCodigoDocumento,
      extra: {
        filasGastos: filasNormalizadas
      }
    }

    try {
      const promises = []

      // UPDATE principal
      promises.push(organizationAPI.updateVinculacion(currentBusiness?.id, idLibro, payload))

      const razonSeleccionada = razonesS?.find(r => r.rut === rut)

      if (razonSeleccionada?.code) {
        const idRazonSocial = razonSeleccionada.code.split('-')[0]

        const payloadRazonSocial = {
          razonSocial
        }

        promises.push(
          organizationAPI.patchRazonSocial(currentBusiness.id, idRazonSocial, payloadRazonSocial)
        )
      } else {
        //
      }

      if (hasCentroCostoChanges) {
        promises.push(
          centroCostosAPI.createLibroCentroCosto(currentBusiness.id, idLibro, centroCostoPayload, temporalidad)
        )
      }

      await Promise.all(promises)

      setSwitchDate(true)
      functionGetData()
      handleClose()
    } catch (error) {
      console.error('ERROR handleSubmit:', error)
    } finally {
      ui.setLoader({ visible: false })
    }
  }

  const handleMassiveUpload = async () => {
    if (isValidatingExcel || excelValidationErrors.length > 0) return
    if (!archivoExcel) {
      setErrorMessage('Debe seleccionar un archivo Excel')
      return
    }

    setErrorMessage(null)

    try {
      const uploadRendicionFile = async () => {
        const validationErrors = await validateRendicionExcel(archivoExcel)
        if (validationErrors.length > 0) {
          throw new Error(validationErrors.join('\n'))
        }
        const response = await businessAPI.uploadRendicionGastosExcel(
          currentBusiness.id,
          archivoExcel
        )
        const responseError = getRendicionResponseError(response)
        if (responseError) {
          throw new Error(responseError)
        }

        return response
      }

      await sileo.promise(uploadRendicionFile, {
        loading: { title: 'Cargando rendicion de gastos' },
        success: {
          title: 'Listo',
          description: (
            <span className='text-green-500/80 font-medium'>
              Rendicion de gastos cargada exitosamente.
            </span>
          )
        },
        error: error => ({
          title: 'Error al cargar',
          description: (
            <span className='text-red-500/80 font-medium'>
              {getMassiveUploadErrorMessage(error)}
            </span>
          )
        })
      })

      setSwitchDate(true)
      handleClose()
    } catch (error) {
      console.error('Error de carga:', error)
      setErrorMessage(getMassiveUploadErrorMessage(error))
    }
  }

  const handleGuardar = () => {
    if (!isManualMode) {
      return handleMassiveUpload()
    }

    return action === 'modificar' ? handleSubmit() : handleDatos()
  }

  const formatMonto = value => {
    if (!value) return ''
    const numericValue = value.replace(/\D/g, '')
    if (!numericValue) return ''
    return new Intl.NumberFormat('es-CL').format(numericValue)
  }

  const formatRut = input => {
    let cleaned = input.replace(/[^0-9kK]/g, '').toUpperCase()
    // Limita a 9 caracteres (8 cuerpo + 1 DV)
    if (cleaned.length > 9) cleaned = cleaned.slice(0, 9)
    const body = cleaned.slice(0, -1)
    const dv = cleaned.slice(-1)
    let formattedBody = ''
    for (let i = body.length - 1, j = 1; i >= 0; i--, j++) {
      formattedBody = body[i] + formattedBody
      if (j % 3 === 0 && i !== 0) {
        formattedBody = '.' + formattedBody
      }
    }
    if (body.length === 0) return dv
    return `${formattedBody}-${dv}`
  }

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    setFecha(today)
  }, [])

  const getCentroCostoOptionsForRow = index => {
    if (!Array.isArray(centroCostos)) return []

    const fromApi = centroCostos
      .filter(cc => !isForbiddenCentroName(cc.name))
      .map(cc => ({
        id: cc.id,
        nombreCuenta: cc.name
      }))
    return fromApi
  }

  const CircleProgress = ({ rows }) => {
    const total = Math.min(
      100,
      rows.reduce((acc, r) => acc + (Number(r.porcentaje) || 0), 0)
    )

    let offset = 0
    const stops = []

    rows.forEach(row => {
      const pct = Math.max(0, Math.min(100 - offset, Number(row.porcentaje) || 0))
      if (pct <= 0) return
      const color = row.color || '#00C6B3'
      stops.push(`${color} ${offset}% ${offset + pct}%`)
      offset += pct
    })

    if (offset < 100) {
      stops.push(`#E5E7EB ${offset}% 100%`)
    }

    const background = `conic-gradient(${stops.join(', ')})`

    return (
      <div className='flex flex-col items-center mt-3'>
        <div
          className='w-20 h-20 rounded-full flex items-center justify-center'
          style={{ background }}
        >
          <span className='text-xs font-semibold'>{total}%</span>
        </div>
        <span className='mt-1 text-[11px] text-gray-500 text-center'>
          Distribución % centros de costo
        </span>
      </div>
    )
  }

  const handleCentroCostoRowChange = (index, field, value) => {
    setCentrosCostoRows(prev => {
      const clone = [...prev]

      if (field === 'porcentaje') {
        // Permitimos string vacío mientras escribe
        const parsed = value === '' ? '' : Math.max(0, Math.min(100, Number(value) || 0))

        clone[index] = {
          ...clone[index],
          porcentaje: parsed
        }
      } else {
        clone[index] = {
          ...clone[index],
          [field]: value
        }
      }
      return clone
    })
    setCentroCostoError('')
  }

  const addCentroCostoRow = () => {
    setCentrosCostoRows(prev => {
      const maxRows = getAvailableCentroCostosCount()
      if (maxRows > 0 && prev.length >= maxRows) {
        return prev
      }

      const nextIndex = prev.length
      const newRow = {
        centroCostoId: '',
        porcentaje: 0,
        color: CENTER_COLORS[nextIndex % CENTER_COLORS.length]
      }
      return [...prev, newRow]
    })
    setCentroCostoError('')
  }

  const removeCentroCostoRow = index => {
    setCentrosCostoRows(prev => {
      return prev.filter((_, i) => i !== index)
    })
    setCentroCostoError('')
  }
  const clearCentroCostoRows = () => {
    setCentrosCostoRows([])
    setCentroCostoError('')
  }
const CustomSelect = ({ label, name, options, value, onChange, disabled }) => (
    <div className='relative flex flex-col items-start'>
      {label && (
        <label
          htmlFor={name}
          className='absolute top-[-15px] left-4 bg-transparent text-xs font-sans font-medium text-gray-500'
        >
          {label}
        </label>
      )}
      <select
        className='h-9 w-full appearance-none focus:outline-none border border-gray-300 hover:border-gray-400 focus:border-gray-400 px-4 py-2 rounded-full shadow leading-tight text-xs font-sans'
        name={name}
        onChange={onChange}
        value={value}
        disabled={disabled}
      >
        <option value='' disabled hidden>
          Seleccione una opción
        </option>
        {options.map(option => (
          <option key={option.id} value={option.id}>
            {option.nombreCuenta}
          </option>
        ))}
      </select>
    </div>
  )

  const maxCentroRows = getAvailableCentroCostosCount()

  return (
    <ResizableModal
      open={open}
      onClose={handleClose}
      footer={
        <>
          <CustomButton
            title='CANCELAR'
            color='primary'
            className='rounded-full'
            onClick={handleClose}
          />
          <CustomButton
            title={
              isManualMode
                ? action === 'replicar'
                  ? 'REPLICAR VINCULACIÓN'
                  : action === 'modificar'
                    ? 'GRABAR VINCULACIÓN'
                    : 'GRABAR VINCULACIÓN'
                : isValidatingExcel ? 'VALIDANDO EXCEL...' : 'CARGAR EXCEL'
            }
            color='accent2'
            className='rounded-full'
            onClick={handleGuardar}
            disabled={!isManualMode && (isValidatingExcel || excelValidationErrors.length > 0)}
          />
        </>
      }
    >
      <>
        {loading ? (
          <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
            <Lottie animationData={LoaderMain} loop autoplay style={{ width: 100, height: 100 }} />
          </Box>
        ) : (
          <>
            <div className='mt-4'>
              <div className='flex justify-between items-start mb-6 gap-4'>
                <div>
                  <Title className='text-3xl font-sans'>Rendición de Gastos</Title>
                </div>
                {canUseMassiveUpload && (
                  <div className='inline-flex rounded-full border border-gray-200 bg-gray-100 p-1 shadow-sm shrink-0'>
                    <button
                      type='button'
                      onClick={() => setModoCarga('manual')}
                      className={`rounded-full px-4 py-2 text-xs font-semibold font-sans uppercase tracking-wide transition-colors ${
                        isManualMode
                          ? 'bg-accent2 text-white shadow-sm'
                          : 'bg-transparent text-gray-500 hover:text-primary'
                      }`}
                    >
                      Carga manual
                    </button>
                    <button
                      type='button'
                      onClick={() => setModoCarga('masiva')}
                      className={`rounded-full px-4 py-2 text-xs font-semibold font-sans uppercase tracking-wide transition-colors ${
                        !isManualMode
                          ? 'bg-accent2 text-white shadow-sm'
                          : 'bg-transparent text-gray-500 hover:text-primary'
                      }`}
                    >
                      Carga masiva
                    </button>
                  </div>
                )}
              </div>
              {isManualMode ? (
                <div className='flex w-full items-start h-full p-6 bg-white rounded-lg shadow-md'>
                <div className='flex flex-col gap-6 w-full'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full'>
                    <InputField label='Folio' value={folio} type='text' disabled />
                    <div>
                      <label className='block text-sm font-sans font-medium text-gray-700'>Fecha</label>
                      <input
                        type='date'
                        value={fecha}
                        onChange={e => setFecha(e.target.value)}
                        className='mt-1 block w-full rounded-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-sans'
                      />
                    </div>
                    <InputField
                      label='Responsable'
                      placeholder='Nombre'
                      type='text'
                      value={razonSocial}
                      onChange={e => setRazonSocial(e.target.value)}
                    />
                    <InputField
                      label='RUT'
                      placeholder='11.111.111-1'
                      type='text'
                      value={formatRut(rut)}
                      onChange={e => setRut(formatRut(e.target.value))}
                    />
                    <InputField disabled={bankAmountLocked}
                      label='Monto'
                      placeholder='$'
                      type='text'
                      value={formatMonto(monto)}
                      onChange={e => setMonto(e.target.value.replace(/[^0-9]/g, ''))}
                    />
                    <InputField
                      label='Clasificación'
                      placeholder='3-2-3-35 Rendiciones de Gastos'
                      type='text'
                      disabled
                    />
                    <TextAreaField
                      label='Detalle'
                      placeholder='Describe en detalle la naturaleza, origen o lo que mejor describa la vinculación que vamos a crear.'
                      value={glosa}
                      onChange={e => setGlosa(e.target.value)}
                    />
                  </div>
                  <TablaRendicionGasto
                    filasGastos={filasGastos}
                    setFilasGastos={setFilasGastos}
                    filaVacia={filaVacia}
                  />
                   {(action === 'modificar' || action === 'replicar') && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                      <div className="md:col-span-1">
                        <CircleProgress rows={centrosCostoRows} />
                      </div>
                      <div className="md:col-span-2">
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <div className="relative flex flex-col">
                              <label className="absolute -top-3 left-4 z-10 bg-white px-1 text-xs font-sans font-medium text-gray-500">
                                Temporalidad centro costos
                              </label>
                              <select
                                className="h-9 w-full border border-gray-300 rounded-full px-4 text-xs font-sans"
                                value={centroCostoTemporalidad}
                                onChange={e => setCentroCostoTemporalidad(Number(e.target.value))}
                                disabled={data.validation === -5}
                              >
                                <option value={0}>Solo este movimiento</option>
                                <option value={1}>Este registro y todos los anteriores</option>
                                <option value={2}>Este registro y todos los posteriores</option>
                                <option value={3}>Aplicar a todos los registros</option>
                              </select>
                            </div>
                          </div>

                          {/* -------- FILAS CENTRO DE COSTO + PORCENTAJE -------- */}
                          <div className="flex flex-col gap-3">
                            {centrosCostoRows.map((row, index) => (
                              <div
                                key={index}
                                className="grid grid-cols-[1fr_160px] gap-3 items-end"
                              >
                                {/* Centro de costo */}
                                <CustomSelect
                                  label="Centro de costo"
                                  name={`centroCosto-${index}`}
                                  options={getCentroCostoOptionsForRow(index)}
                                  value={row.centroCostoId}
                                  onChange={e =>
                                    handleCentroCostoRowChange(index, 'centroCostoId', e.target.value)
                                  }
                                  disabled={data.validation === -5}
                                />

                                {/* Porcentaje */}
                                <div>
                                  <label className="text-xs text-gray-500 font-sans">Porcentaje</label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      min={0}
                                      max={100}
                                      className="h-9 w-full border border-gray-300 rounded-full px-3 text-xs font-sans"
                                      value={row.porcentaje === 0 ? '' : row.porcentaje}
                                      onChange={e =>
                                        handleCentroCostoRowChange(index, 'porcentaje', e.target.value)
                                      }
                                      disabled={data.validation === -5}
                                    />

                                    {centrosCostoRows.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => removeCentroCostoRow(index)}
                                        className="text-red-500 hover:text-red-700"
                                        title="Eliminar centro de costo"
                                      >
                                        <ClearOutlinedIcon fontSize="small" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}

                            {/* -------- AGREGAR -------- */}
                            <div className='flex w-full items-center justify-between font-sans'>
                              <button
                                type="button"
                                className={`text-xs ${
                                  maxCentroRows > 0 && centrosCostoRows.length >= maxCentroRows
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-accent3'
                                }`}
                                onClick={addCentroCostoRow}
                                disabled={
                                  data.validation === -5 ||
                                  (maxCentroRows > 0 && centrosCostoRows.length >= maxCentroRows)
                                }
                              >
                                + Agregar centro de costo
                              </button>
                              <button
                                type='button'
                                className={`text-xs ${
                                  data.validation === -5 || centrosCostoRows.length === 0
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-red-500 hover:text-red-600'
                                }`}
                                onClick={clearCentroCostoRows}
                                disabled={data.validation === -5 || centrosCostoRows.length === 0}
                              >
                                - Quitar asignación
                              </button>
                            </div>
                          </div>

                          {centroCostoError && (
                            <p className="text-xs text-red-500 font-sans">{centroCostoError}</p>
                          )}
                        </div>
                      </div>
                     </div>
                  )}
                </div>
                </div>
              ) : (
                <MassiveUploadContent
                  archivoExcel={archivoExcel}
                  onFileSelect={handleArchivoExcelChange}
                  errorMessage={excelValidationErrors.length ? excelValidationErrors.join('\n') : errorMessage}
                  isDownloadingTemplate={isDownloadingTemplate}
                  onTemplateDownload={handleDownloadTemplate}
                />
              )}
            </div>
            {/*{openTabla && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-4xl max-h-[120vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <Title className="text-xl">Registro</Title>
              <button
                onClick={() => setOpenTabla(false)}
                className="text-gray-500 hover:text-black text-2xl"
              >
                &times;
              </button>
            </div>

            <TablaRendicionGasto
              filasGastos={filasGastos}
              setFilasGastos={setFilasGastos}
            />
          </div>
        </div>
      )}*/}
          </>
        )}
      </>
    </ResizableModal>
  )
}

export default RendicionDeGastos
